/// <reference types="vite/client" />
/**
 * Firebase Infrastructure Config (TypeScript)
 * 
 * Centralized Firebase initialization with persistence and multi-tab support.
 * Consolidated from legacy config to ensure stability and path alias compatibility.
 */

import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { 
  getFirestore, 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager,
  memoryLocalCache,
  Firestore
} from 'firebase/firestore';
import { getAuth, GoogleAuthProvider, Auth } from 'firebase/auth';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, Analytics } from 'firebase/analytics';

// Hardcoded fallback values (Matching: fnbsystem-2cmarketing)
const FALLBACK_CONFIG = {
  apiKey: "AIzaSyD50AlfQL7tThw3uQVXRgWjyKgqzKjvRPk",
  authDomain: "fnbsystem-2cmarketing.firebaseapp.com",
  projectId: "fnbsystem-2cmarketing",
  storageBucket: "fnbsystem-2cmarketing.firebasestorage.app",
  messagingSenderId: "798275341839",
  appId: "1:798275341839:web:ae5bae9699393e797d9d7a",
  measurementId: "G-6C7JJ0HPTK"
};

// Cấu hình Firebase - Lấy từ biến môi trường hoặc config cũ
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || FALLBACK_CONFIG.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || FALLBACK_CONFIG.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || FALLBACK_CONFIG.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || FALLBACK_CONFIG.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || FALLBACK_CONFIG.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || FALLBACK_CONFIG.appId,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || FALLBACK_CONFIG.measurementId
};

// Log which config source is being used (dev only)
if (import.meta.env.DEV) {
  const envKey = import.meta.env.VITE_FIREBASE_API_KEY;
  console.log("[Firebase] Config Status:", {
    source: !envKey ? "Fallback (Hardcoded)" : "Environment Variables",
    projectId: firebaseConfig.projectId
  });
}

// 1. Initialize Main App
let app: FirebaseApp;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApp();
}

// Initialize Secondary App for Staff Creation (to not log out main user)
let secondaryApp: FirebaseApp;
try {
  secondaryApp = getApp("SecondaryApp");
} catch {
  secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
}

// 2. Initialize Firestore with Robust Caching
// Fix 'Internal Assertion Failed' by using explicit tab manager and memory fallback
let dbInstance: Firestore;
try {
  // Use persistentMultipleTabManager for better stability in multi-tab environments
  dbInstance = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
      cacheSizeBytes: 100 * 1024 * 1024 // 100MB limit
    })
  });
  if (import.meta.env.DEV) console.log("[Firebase] Firestore initialized with Multiple Tab Persistence.");
} catch (e: any) {
  // If already initialized or if persistence failed, fallback
  if (e.code === 'failed-precondition' || e.message?.includes('already initialized')) {
    dbInstance = getFirestore(app);
  } else {
    console.error("[Firebase] Persistence failed, falling back to Memory Cache:", e);
    try {
      dbInstance = initializeFirestore(app, {
        localCache: memoryLocalCache()
      });
    } catch {
      dbInstance = getFirestore(app);
    }
  }
}

export const db: Firestore = dbInstance;
export const auth: Auth = getAuth(app);
export const secondaryAuth: Auth = getAuth(secondaryApp);
export const storage: FirebaseStorage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

// 3. Analytics (Client-side only)
let analyticsInstance: Analytics | null = null;
if (typeof window !== "undefined") {
  try {
    analyticsInstance = getAnalytics(app);
  } catch (error) {
    console.warn("[Firebase] Analytics initialization skipped/blocked.");
  }
}

/**
 * Check if Firestore is likely blocked by a client-side extension (AdBlocker)
 */
export const isFirestoreBlocked = async (): Promise<boolean> => {
  if (typeof window === "undefined") return false;
  try {
    await fetch("https://firestore.googleapis.com", {
      mode: 'no-cors',
      cache: 'no-store'
    });
    return false;
  } catch (error) {
    console.warn("[Firebase] Firestore connectivity check failed:", error);
    return true;
  }
};

export const analytics = analyticsInstance;
export default app;
